import { Link } from 'react-router';
import { formatMoney, isCurrencyCode, money } from '@ecobox/domain';
import {
  COMMAND_LABELS,
  formatAddress,
  navigateUrl,
  telUrl,
  type CommandProblem,
  type OpsRun,
  type OpsStop,
  type StopGeo,
} from '@ecobox/firebase';
import { Badge, Button, type BadgeTone } from '@ecobox/ui';
import { useServices } from '../../services';

/** An amount in a currency code that may be unknown to the phone. Never a float: minor units in, text out. */
export function fmt(minor: number, currency: string): string {
  return isCurrencyCode(currency)
    ? formatMoney(money(minor, currency))
    : `${(minor / 100).toFixed(2)} ${currency}`;
}

export function Tile({
  n,
  label,
  tone,
}: {
  n: number | string;
  label: string;
  tone?: 'ok' | 'wait' | 'bad' | undefined;
}) {
  return (
    <div className="ops-tile" data-tone={tone}>
      <div className="ops-tile__n">{n}</div>
      <div className="ops-muted">{label}</div>
    </div>
  );
}

export function Progress({ percent, label }: { percent: number; label: string }) {
  return (
    <div
      className="ops-progress"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      <span style={{ width: `${percent}%` }} />
    </div>
  );
}

const RUN_STATUS: Record<OpsRun['status'], { label: string; tone: BadgeTone }> = {
  planned: { label: 'Ready to start', tone: 'neutral' },
  assigned: { label: 'Ready to start', tone: 'neutral' },
  active: { label: 'On the road', tone: 'info' },
  closed: { label: 'Finished', tone: 'success' },
};

export const runStatus = (status: OpsRun['status']) => RUN_STATUS[status];

export function stopStatus(
  status: OpsStop['status'],
  waiting: boolean,
): { label: string; tone: BadgeTone } {
  const base: Record<OpsStop['status'], { label: string; tone: BadgeTone }> = {
    pending: { label: 'To do', tone: 'neutral' },
    arrived: { label: 'Arrived', tone: 'info' },
    done: { label: 'Done', tone: 'success' },
    failed: { label: 'Failed', tone: 'danger' },
    skipped: { label: 'Skipped', tone: 'neutral' },
  };
  const s = base[status];
  return waiting ? { label: `${s.label} · waiting to sync`, tone: 'warning' } : s;
}

/** Navigate (maps) and call buttons. They are links, so they work in any phone's own apps. */
export function ContactLinks({
  geo,
  address,
  phones,
  label = '',
}: {
  geo?: StopGeo | undefined;
  address?: Parameters<typeof formatAddress>[0];
  phones: readonly string[];
  label?: string;
}) {
  const href = navigateUrl({ geo, address: formatAddress(address) || label });
  return (
    <div className="ops-links">
      {href ? (
        <a
          className="eb-btn"
          data-variant="secondary"
          data-size="touch"
          data-block="true"
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          Navigate
        </a>
      ) : null}
      {phones.slice(0, 2).map((p) => (
        <a
          key={p}
          className="eb-btn"
          data-variant="secondary"
          data-size="touch"
          data-block="true"
          href={telUrl(p)}
          aria-label={`Call ${p}`}
        >
          Call {p}
        </a>
      ))}
    </div>
  );
}

/** Commands the server refused, or that never arrived: shown with the reason and what to do. */
export function Problems({
  problems,
  stopId,
}: {
  problems: readonly CommandProblem[];
  stopId?: string | undefined;
}) {
  const { writer } = useServices();
  const shown = stopId === undefined ? problems : problems.filter((p) => p.stopId === stopId);
  if (shown.length === 0) return null;
  return (
    <ul className="ops-list" aria-label="Problems">
      {shown.map((p) => (
        <li key={p.commandId} className="ops-row" data-tone="bad" role="alert">
          <div className="ops-row__top">
            <strong>{COMMAND_LABELS[p.type] ?? p.type}</strong>
            <Badge tone="danger">{p.state === 'rejected' ? 'Refused' : 'Not delivered'}</Badge>
          </div>
          <div className="ops-detail">{p.message}</div>
          <div className="ops-row__actions">
            {p.state === 'not_delivered' ? (
              <Button size="lg" variant="secondary" onClick={() => void writer.resend(p.commandId)}>
                Resend
              </Button>
            ) : (
              <Button size="lg" variant="ghost" onClick={() => void writer.dismiss(p.commandId)}>
                Dismiss
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function BackLink({ to, children }: { to: string; children: string }) {
  return (
    <Link to={to} className="ops-muted">
      ← {children}
    </Link>
  );
}
